import { CHAT_STATE, EMAIL_STATE } from "../model/Constants";
import { log } from "../chatframe/Utils";

/* eslint-disable no-param-reassign,wrap-iife */
export function isIOS(): boolean {
    return /(iPad|iPhone|iPod)/g.test(window.navigator.userAgent);
}

export function isAndroid(testString?: any): boolean {
    let matched = new RegExp("^(?!.*windows)(?=.*android)", "i").test(window.navigator.userAgent);

    if (matched && testString != null) {
        matched = window.navigator.userAgent.indexOf(testString) != -1;
    }
    return matched;
}

export function isSafari(): boolean {
    const ua = window.navigator.userAgent.toLowerCase();
    return ua.indexOf("safari") >= 0
        && !(ua.indexOf("chrome") >= 0
            || ua.indexOf("crios") >= 0);
}

export function getUrlParams(url: string) {
    const params = {};

    if (typeof url === "string") {
        /** @type {number} */
        const startIndex = url.indexOf("?");

        if (startIndex >= 0) {
            /** @type {Array.<string>} */
            const paramsArray = url.slice(startIndex + 1).split("&");

            /** @type {Array.<string>} */
            let keyValuePair;

            for (let i = 0; i < paramsArray.length; i++) {
                keyValuePair = paramsArray[i].split("=");
                params[keyValuePair[0]] = keyValuePair[1];
            }
        }
    }

    return params;
}

/**
 * @param {Object} obj - The target object to search through
 * @param {String} path - The object path to look for ex: item.location.lat
 * @returns {Any} - Returns the value set at the given object path
 *
 * Ex:
 *  item :{
 *      name: 'keys',
 *      location:{
 *          long: 45.5,
 *          lat: 89.8
 *      }
 *  }
 *  getDeepValue(item, "location.lat") == 89.8
 */
export function getDeepValue(obj: any, path: any): any {
    path = path.split(".");

    for (let i = 0, len = path.length; i < len; i++) {
        obj = obj[path[i]];
    }

    return obj;
}

/**
 *
 * @param {Object} obj - Object to set the value to
 * @param {String} path - The object path to set the value to
 * @param {Any} value - The value to set
 * @returns {boolean} - true if the value was set, false if the path was not found.
 */
export function setDeepValue(obj: any, path: any, value: any): boolean {
    let lObj = obj;
    let lPath = path;
    path = path.split(".");

    for (let i = 0, len = path.length; i < len; i++) {
        lObj = obj;
        lPath = path[i];
        obj = obj[path[i]];
    }

    if (lObj[lPath] !== undefined) {
        lObj[lPath] = value;

        return true;
    }

    return false;
}

/**
 *
 * @param {Object} prop
 * @param {CIApp} appInstance
 */

export function getChimeImage(prop: any, appInstance: any): any {
    const val = prop.imgsrc && prop.imgsrc !== "undefined" ? prop.imgsrc : (prop.imgname || "");
    const imgObj: any = { type: "Chime" };
    const isAudioImagesExist = !!(prop.audioonimgname !== "undefined" && prop.audiooffimgname !== "undefined");

    if (isAudioImagesExist) {
        if (appInstance.isSoundEnabled()) {
            imgObj.imgname = prop.audiooffimgname;
        } else {
            imgObj.imgname = prop.audioonimgname;
        }
    } else if (val) {
        const li = val.lastIndexOf(".");
        const chimeFile = val.substring(0, li)
            + (appInstance.isSoundEnabled() ? "_off" : "_on")
            + val.substring(li, val.length);

        if (prop.imgsrc && prop.imgsrc !== "undefined") {
            imgObj.imgsrc = chimeFile;
        } else if (chimeFile) {
            imgObj.imgname = chimeFile;
        }
    }
    return imgObj;
}

export function getChimeText(configText: any, appInstance: any): any {
    /**
     * For usability opposite  of sound state is displayed in the menu,
     * ie when sound state is on , sound off text, sound off button is displayed to user
     */
    const audioTextOff = appInstance.audioTextOff || `${configText} ${"off"}`;
    const audioTextOn = appInstance.audioTextOn || `${configText} ${"on"}`;
    return appInstance.isSoundEnabled() ? audioTextOff : audioTextOn;
}

/**
 * Adding chimeon/chimeoff class to element
 * @export addChimeToggleClass
 * @param {Boolean} isSoundEnabled - is sound is enabled on that element
 * @param {HTMLElement} element
 */
export function addChimeToggleClass(isSoundEnabled: any, element: any): void {
    element.classList.remove("chimeon", "chimeoff");
    element.classList.add(isSoundEnabled ? "chimeoff" : "chimeon");
}

/**
 * Setting styles properties to HTMLElement style
 * @export setStylesOnElement
 * @param {Object} styles - styles properties in a JSON object
 * @param {HTMLElement} element
 */
export function setStylesOnElement(styles: any, element: any): void {
    Object.assign(element.style, styles);
}

/**
 * @export textGenerator
 * @example
 * // agent  %chatroom.member.name% joins the conversation
 * @param {string} str - string that will be formatted, all the references to '%<key>%' will be replaced with appropriate key value from objInstance
 * @param {com.inq.flash.messagingframework.Message#data} objInstance - json object where %key%  references from string is matched to find the values
 * @returns {string}
 */
export function textGenerator(str: string, objInstance: any): any {
    if (str) {
        str = str.replace(/%(\S+)%/g, function (all, match1) {
            return objInstance[match1] || match1;
        });
    }
    return str;
}

/**
 * @export generateBackgroundImgUrl
 * Creates a url value that can be used to set in a background-image style property
 * @param {ImageO} propObj
 * @param {CIApp} $app
 * @returns {string}
 */
export function generateBackgroundImgUrl(propObj: any, $app: any): string {
    const url = generateImagePath(propObj, $app);

    if (url) {
        return `url('${url}')`;
    }
    return "";
}

/**
 * Adding css rule to css stylesheet element
 * @export addCSSRule
 * @param {CSSStyleSheet} sheet
 * @param {String} selector
 * @param {String} rules
 * @param {number} index
 */
export function addCSSRule(sheet: CSSStyleSheet, selector: string, rules: string, index?: any): void {
    if ("insertRule" in sheet) {
        sheet.insertRule(`${selector}{${rules}}`, index);
    } else if ("addRule" in sheet) {
        sheet.addRule(selector, rules, index);
    }
}


/**
 * Decode html html entity characters to the correct character entities
 * Ref: https://stackoverflow.com/a/7394787/438371
 * 
 * @param {string} str
 * @returns {string}
 */
export const decodeEntities = (function() {
    // prevent overhead from creating the object each time
    const element = document.createElement('div');
    function decodeAndReplaceHTMLEntities(str:string) {
        str = str.replace(/\&amp;/g, "&");
        return str.replace(/\&([^\s;]+);/g, function(entity:string) {

            element.innerHTML = entity;
            entity = element.textContent;
            element.innerHTML = '';

            return entity;
        });
    }

    return decodeAndReplaceHTMLEntities;
})();

export function debounce(func: any, wait: number, immediate?: any): (A?: any) => void {
    let timeout: any;

    return function executedFunction(param?: any) {

        if (param === "clear-timer") {
            clearTimeout(timeout);
            return;
        }

        const context = this;
        const args = arguments;

        const later = function () {
            timeout = null;

            if (!immediate) {
                func.apply(context, args);
            }
        };

        const callNow = immediate && !timeout;

        clearTimeout(timeout);

        timeout = setTimeout(later, wait);

        if (callNow) {
            func.apply(context, args);
        }
    };
}

export function isValidColor(color: string): boolean {
    return /^#([0-9a-f]{3}){1,2}$/i.test(color);
}

/**
 * Creates the path to the image
 * @export generateImagePath
 * @param {ImageO} propObj
 * @param {CIApp} $app
 * @returns {string}
 */
export function generateImagePath(propObj: any, $app: any): any {
    let imgurl;

    if ((propObj.imgsrc && propObj.imgsrc !== "undefined") || propObj.imgSrc) {
        imgurl = propObj.imgsrc || propObj.imgSrc;
    } else if ((propObj.imgname && propObj.imgname !== "undefined") || propObj.imgName) {
        imgurl = `${$app.getImagePath()}${propObj.imgname || propObj.imgName}`;
    }
    return imgurl;
}

const monthNames: string[] = ["January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December",
];

const monthAbr: string[] = ["Jan", "Feb", "Mar", "Apr", "May", "Jun",
    "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

/**
 * converts a given date millisecond value to the given format
 * @export dateFormatter
 * @example
 * // April 3, 2020 - month dd, yyyy
 * @param {number} time
 * @param {string} format
 * @returns {string}
 */
export function dateFormatter(time: string | number | Date, format: any): string {
    const t = new Date(time);

    const tf = function (i: number) {
        return (i < 10 ? "0" : "") + i;
    };

    let ampm = "";
    const val = format.replace(/yyyy|YYYY|MMM|MM|DD|dd|HH|H|hh|h|mm|ss|month/g, function (a: string) {
        switch (a) {
            case "yyyy":
                return tf(t.getFullYear());

            case "YYYY":
                return tf(t.getFullYear());

            case "MMM":
                return monthAbr[t.getMonth()];

            case "MM":
                return tf(t.getMonth() + 1);

            case "mm":
                return tf(t.getMinutes());

            case "dd":
                return tf(t.getDate());

            case "DD":
                return tf(t.getDate());

            case "HH":

            case "H": {
                const h = t.getHours();
                return a == "H" ? h : tf(h);
            }

            case "hh":

            case "h": {
                let h = t.getHours();
                h = (h + 24) % 24;
                ampm = " AM";

                if (h == 0) { // At 00 hours we need to show 12 am
                    h = 12;
                } else if (h >= 12) {
                    h = h % 12;

                    if (h == 0) {
                        h = 12;
                    }

                    ampm = " PM";
                }
                return a == "h" ? h : tf(h);
            }

            case "ss":
                return tf(t.getSeconds());

            case "month":
                return monthNames[t.getMonth()];

            // no default
        }
        return a;
    });

    return val + ampm;
}

/**
 * Clones a json object
 * @param o {Object}
 * @return {Object}
 */
export const clone = (o: any): any => JSON.parse(JSON.stringify(o));

/**
 * Checks if the given value is an object
 * @param val
 * @return {boolean}
 */
export const isObject = (val: any) => val != null && typeof val === "object" && Array.isArray(val) === false;

/**
 * Checks if both a and b are equal
 * @param a
 * @param b
 * @return {boolean}
 */
export const isEqual = (a: any, b: any) => JSON.stringify(a) === JSON.stringify(b);

/**
 * scroll the transcript element all the way to bottom
 * @export scrollToBottom
 * @param {Transcript} element
 */
export function scrollToBottom(element: any): void {
    element.scrollTop = element.scrollHeight;
}

/**
 * Does a smooth scrolling to the bottom of the {@link Transcript} Element
 * @export smoothScrollToBottom
 * @param {Transcript} element
 * @param {number} duration
 */

export function smoothScrollToBottom(element: any, duration: any): Promise<void> | { interrupt: () => void; p: Promise<unknown>; } {
    return smoothScrollToTarget(element, element.scrollHeight, duration);
}

/**
 * Calculates the distance that chat window must be scrolled and then smoothly scrolls down
 * @param {HTMLElement} element
 * @param {number} target
 * @param {number} duration
 * @return {Promise<any>|Promise<void>|{p: Promise, interrupt: Function}}
 */
export function smoothScrollToTarget(element: any, target: number, duration: number): Promise<void> | { interrupt: () => void; p: Promise<unknown>; } {
    target = Math.round(target);
    duration = Math.round(duration);

    if (duration < 0) {
        return Promise.reject("bad duration"); // eslint-disable-line   prefer-promise-reject-errors
    }

    const start_time = Date.now(); // eslint-disable-line  camelcase
    const end_time = start_time + duration; // eslint-disable-line  camelcase

    const start_top = element.scrollTop; // eslint-disable-line  camelcase
    const distance = target - start_top - element.clientHeight; // eslint-disable-line  camelcase

    // based on http://en.wikipedia.org/wiki/Smoothstep
    const smooth_step = function (start: number, end: number, point: number) { // eslint-disable-line  camelcase
        if (point <= start) {
            return 0;
        }

        if (point >= end) {
            return 1;
        }

        const x = (point - start) / (end - start); // interpolation
        return x * x * (3 - 2 * x);
    };

    let interrupt = false;
    let p: Promise<any>;

    if (duration === 0) {
        element.scrollTop = target;
        p = Promise.resolve();
    } else {
        p = new Promise(function (resolve, reject) {
            /*
            * This is to keep track of where the element's scrollTop is
            * supposed to be, based on what we're doing
            */
            let previous_top = element.scrollTop; // eslint-disable-line  camelcase

            // This is like a think function from a game loop
            var scroll_frame = function () { // eslint-disable-line  camelcase
                /*
                * if(element.scrollTop != previous_top) {
                *    reject("interrupted");
                *    return;
                * }
                */

                if (interrupt) {
                    reject("interrupted"); // eslint-disable-line   prefer-promise-reject-errors
                    return;
                }

                // set the scrollTop for this frame
                const now = Date.now();
                const point = smooth_step(start_time, end_time, now); // eslint-disable-line  camelcase
                const frameTop = Math.round(start_top + (distance * point)); // eslint-disable-line  camelcase
                element.scrollTop = frameTop;

                // check if we're done!
                if (now >= end_time) { // eslint-disable-line  camelcase
                    element.scrollTop = start_top + distance;
                    resolve(undefined);
                    return;
                }

                /*
                * If we were supposed to scroll but didn't, then we
                * probably hit the limit, so consider it done; not
                * interrupted.
                */
                if (element.scrollTop === previous_top // eslint-disable-line  camelcase
                    && element.scrollTop !== frameTop) {
                    element.scrollTop = start_top + distance;
                    resolve(undefined);
                    return;
                }

                previous_top = element.scrollTop; // eslint-disable-line  camelcase

                /*
                * schedule next frame for execution
                * setTimeout(scroll_frame, 0);
                */
                window.raf(scroll_frame);
            };

            /*
            * boostrap the animation process
            * setTimeout(scroll_frame, 0);
            */
            window.raf(scroll_frame);
        });
    }

    return {
        interrupt: () => {
            interrupt = true;
        },
        p: p,
    };
}

/**
 * Makes a smooth mouse wheel scrolling of chat transcript items
 * @link https://stackoverflow.com/questions/47011055/smooth-vertical-scrolling-on-mouse-wheel-in-vanilla-javascript
 * @param {HTMLDivElement} target
 * @param {number} speed
 * @param {number} smooth
 * @constructor
 */
export function SmoothScroll(target: any, speed: any, smooth: any): void {
    if (target === document) {
        target = (document.scrollingElement
            || document.documentElement
            || document.body.parentNode
            || document.body); // cross browser support for document scrolling
    }

    let moving = false;
    let paused = false;
    let lastY: number;
    let pos = target.scrollTop;
    const frame = target === document.body
        && document.documentElement
        ? document.documentElement
        : target; // safari is the new IE

    if (isIOS()) {
        target.addEventListener("touchstart", touchstart, { passive: false });
        target.addEventListener("touchmove", touchend, { passive: false });
    } else {
        target.addEventListener("mousewheel", scrolled, { passive: false });
        target.addEventListener("DOMMouseScroll", scrolled, { passive: false });
    }

    function touchstart(e: any): void {
        const currentY = e.touches[0].clientY;
        lastY = currentY;
    }

    function touchend(e: any): void {
        e.preventDefault();

        if (paused) {
            return;
        }

        const currentY = e.changedTouches[0].clientY;
        const delta = currentY - lastY;

        pos = target.scrollTop;
        pos += (delta * speed) * -1;
        pos = Math.max(0, Math.min(pos, target.scrollHeight - frame.clientHeight));// limit scrolling
        lastY = currentY;

        if (!moving) {
            update();
        }
    }

    function scrolled(e: any): void {
        e.preventDefault(); // disable default scrolling

        if (paused) {
            return;
        }

        const delta = normalizeWheelDelta(e);
        pos = target.scrollTop;
        pos += Math.round(-delta * speed);
        pos = Math.max(0, Math.min(pos, target.scrollHeight - frame.clientHeight));// limit scrolling

        if (!moving) {
            update();
        }
    }

    function normalizeWheelDelta(e: any): number {
        if (e.detail) {
            if (e.wheelDelta) {
                return e.wheelDelta / e.detail / 40 * (e.detail > 0 ? 1 : -1); // eslint-disable-line  no-mixed-operators
            }
            return -e.detail / 3; // Firefox
        }
        return e.wheelDelta / 120; // IE,Safari,Chrome
    }

    function update(): void {
        if (paused) {
            moving = false;
            return;
        }

        moving = true;

        const distance = pos - target.scrollTop;
        const delta = distance / smooth;

        target.scrollTop += delta;

        if (Math.abs(delta) >= 1) {
            requestFrame(update);
        } else if (Math.abs(delta) < 1 && Math.abs(delta) > 0) {
            // chrome has problem when scroll top is assigned with value that is less than 1
            if (Math.abs(distance) <= 1) {
                target.scrollTop = pos;
                moving = false;
            } else {
                target.scrollTop += Math.ceil(delta);
                requestFrame(update);
            }
        } else {
            moving = false;
        }
    }

    /**
     * @member {function}
     */
    this.pause = function () {
        paused = true;
    };

    /**
     * @member {function}
     */
    this.resume = function () {
        paused = false;
    };

    /**
     * @member {function}
     */

    this.reset = function () {
        target.removeEventListener("mousewheel", scrolled, { passive: false });
        target.removeEventListener("DOMMouseScroll", scrolled, { passive: false });
    };

    this.resume();

    var requestFrame = function () { // requestAnimationFrame cross browser
        return (
            window.requestAnimationFrame
            || window.webkitRequestAnimationFrame
            || window.mozRequestAnimationFrame
            || window.oRequestAnimationFrame
            || window.msRequestAnimationFrame
            || function (func: TimerHandler) {
                window.setTimeout(func, 1000 / 50);
            }
        );
    }();
}

/**
 * Utility setTimeout handler, It gives handlers for various states of the Timeout like  executed, pending ,paused etc
 * @export Timeout
 * @type {{resume: resume, paused: (function(*=): *), set: (function(...[*]): function(): *), restart: restart, pending: (function(*=): (*|boolean)), clear: clear, exists: (function(*): boolean), executed: (function(*): boolean), instantiate: (function(*=, *=, ...[*]): {resume: (function(): (boolean|(function(): *))), paused: (function(): *), set: (function(*=, *=, ...[*]): function(): *), restart: (function(): (boolean|(function(): *))), pending: (function(): *), clear: (function(*=): void), exists: (function(): *), executed: (function(): *), pause: (function(): (boolean|*)), remaining: (function(): number)}), pause: pause, remaining: remaining}}
 */
export const Timeout = (() => {
    const keyId = {};
    const originalMs = {};
    const metadata = {};

    const clear = (key: string, erase = true) => {
        clearTimeout(keyId[key]);
        delete keyId[key];
        const f = metadata[key] && metadata[key].func;

        if (erase) {
            delete metadata[key];
            delete originalMs[key];
        }
        return f;
    };

    /*
     * set(key, func, ms = 0, [param1, param2, ...]) -- user-defined key
     * set(func, ms = 0, [param1, param2, ...]) -- func used as key
     *
     * returns a function allowing you to test if it has executed
     */
    const set = (...args: any[]) => {
        let key: any;
        let func: any;
        let ms: any;
        let params: any;

        if (args.length === 0) {
            throw Error("Timeout.set() requires at least one argument");
        }

        if (typeof args[1] === "function") {
            [key, func, ms, ...params] = args;
        } else {
            [func, ms, ...params] = args;
            key = func.toString();
        }

        if (!func) {
            throw Error("Timeout.set() requires a function parameter");
        }

        clear(key);

        const invoke = () => (metadata[key] = false, func(...params)); // eslint-disable-line  no-return-assign,no-sequences

        keyId[key] = setTimeout(invoke, ms || 0);
        originalMs[key] = originalMs[key] || ms;

        metadata[key] = { // eslint-disable-line  object-shorthand
            func,
            key,
            ms,
            params,
            paused: false,
            startTime: new Date().getTime(),
            timeSpentWaiting: 0,
        };

        return () => executed(key);
    };

    // timeout has been created
    const exists = (key: string) => key in keyId || metadata[key] !== undefined;

    // test if a timeout has run
    const executed = (key: string) => metadata[key] === false;

    // timeout does exist, but has not yet run
    const pending = (key: string) => exists(key) && !executed(key);

    // timeout does exist, but will not execute because it is paused
    const paused = (key: string) => exists(key) && metadata[key].paused;

    // pause our execution countdown until we're ready for it to resume
    const pause = (key: string) => {
        if (!metadata[key] || paused(key)) {
            return false;
        }

        clear(key, false);

        metadata[key].paused = true;
        metadata[key].timeSpentWaiting = new Date().getTime() - metadata[key].startTime;

        return metadata[key].timeSpentWaiting;
    };

    const resume = (key: string) => {
        if (!metadata[key]) {
            return false;
        }

        const { func, ms, params, paused, timeSpentWaiting, // eslint-disable-line  no-shadow
        } = metadata[key];

        if (!paused) {
            return false;
        }

        const remainingTime = ms - timeSpentWaiting;
        return set(key, func, remainingTime, ...params);
    };

    const remaining = (key: string) => {
        if (!metadata[key]) {
            return 0;
        }

        const { ms, startTime, timeSpentWaiting } = metadata[key];

        return paused(key)
            ? ms - timeSpentWaiting
            : Math.max(0, startTime + ms - new Date().getTime());
    };

    const restart = (key: string) => {
        if (!metadata[key]) {
            return false;
        }

        const { func, params, paused } = metadata[key]; // eslint-disable-line  no-shadow

        clear(key, false);

        if (paused) {
            metadata[key].paused = false;
        }

        return set(key, func, originalMs[key], ...params);
    };

    const instantiate = (ctorFunc: any, ctorMs = 0, ...ctorCallbackParams: any[]) => {
        if (!ctorFunc) {
            throw Error("Timeout.instantiate() requires a function parameter");
        }

        const key = `${Math.random()}${ctorFunc}`.replace(/\s/g, "");

        set(key, ctorFunc, ctorMs, ...ctorCallbackParams);

        return {
            clear: (erase = true) => clear(key, erase),
            executed: () => executed(key),
            exists: () => exists(key),
            pause: () => pause(key),
            paused: () => paused(key),
            pending: () => pending(key),
            remaining: () => remaining(key),
            restart: () => restart(key),
            resume: () => resume(key),
            set: (newFunc: string | number, newMs = 0, ...newParams: any) => set(key, newFunc, newMs, ...newParams),
        };
    };

    return {
        clear,
        executed,
        exists,
        instantiate,
        pause,
        paused,
        pending,
        remaining,
        restart,
        resume,
        set,
    };
})();

/**
 * request animation frame polyfill
 */
(function (window, request, cancel) {
    let lastTime = 0;
    const strings = {
        raf: "requestAnimationFrame",
        caf: "cancelAnimationFrame",
        af: "AnimationFrame",
    };

    /**
     * requestAnimationFrame shim layer with setTimeout fallback
     * @see http://paulirish.com/2011/requestanimationframe-for-smart-animating
     * modified for use with raf shim and raf based setInterval/Timeout
     */
    const vendors = ["ms", "moz", "webkit", "o"];

    for (let x = 0; x < vendors.length && !window[strings.raf]; ++x) {
        window[strings.raf] = window[`${vendors[x]}Request${strings.af}`];
        window[strings.caf] = window[`${vendors[x]}Cancel${strings.af}`] || window[`${vendors[x]}CancelRequest${strings.af}`];
    }

    if (!window[strings.raf]) {
        window[strings.raf] = function (callback: any): number {
            const currTime = new Date().getTime();
            const timeToCall = Math.max(0, 16 - (currTime - lastTime));
            const id = window.setTimeout(function () {
                callback(currTime + timeToCall);
            },
                timeToCall);
            lastTime = currTime + timeToCall;
            return id;
        };
    }

    if (!window[strings.caf]) {
        window[strings.caf] = function (id: number): void {
            window.clearTimeout(id);
        };
    }

    /* ^---------- end of polyfill, start of raf shim ------------> */

    const queue: { guid: number; cancel: boolean; fn: any; }[] = [];
    const lookup = {};
    let guid = 0;

    const _rAF = window[strings.raf];
    const _cAF = window[strings.caf]; // eslint-disable-line no-unused-vars

    function clearQueue(time: any): void {
        if (!queue.length || !raf.running) {
            raf.running = false;
            return;
        }

        const todo = queue.splice(0); // move items across
        let i = 0;
        let item = null;
        const length = todo.length;

        for (; i < length; i++) {
            item = todo[i];

            if (!item.cancel) {
                item.fn(time);
            }

            delete lookup[item.guid];
        }

        _rAF(clearQueue);
    }

    function raf(fn: any): any {
        guid++;

        lookup[guid] = fn;
        /* eslint object-shorthand: [2, "consistent"] */
        queue.push({
            guid: guid,
            cancel: false,
            fn: fn,
        });

        if (!raf.running) {
            raf.running = true;
            _rAF(clearQueue);
        }
        return guid;
    }

    raf.running = true;

    raf.cancel = function (id: string | number): boolean {
        if (lookup[id]) {
            lookup[id].cancel = true;
            return true;
        }

        return false;
    };

    raf.fps = function (fn: any, fps: number): void {
        let lastFrameTime = 0;
        const ms = 1000 / fps;

        function update(elapsedTime?: number): void {
            // calculate the delta since the last frame
            const delta = elapsedTime - (lastFrameTime || 0);

            // queue up an rAF update call
            raf(update);

            /*
             * if we *don't* already have a first frame, and the
             * delta is less than 33ms (30fps in this case) then
             * don't do anything and return
             */
            if (lastFrameTime && delta < ms) {
                return;
            }

            // else we have a frame we want to update at our fps...

            /*
             * capture the last frame update time so we can work out
             * a delta next time.
             */
            lastFrameTime = elapsedTime;

            // now do the frame update and render work
            fn(elapsedTime);
        }

        update();
    };

    if (cancel) {
        window[cancel] = raf.cancel;
    }

    if (!request) {
        request = "raf";
    }

    _rAF(clearQueue);

    window[request] = raf;
})(window, "raf", "caf");

/**
 * Converts the property and value of given keys from an Object to String
 * @param {Array} keys
 * @param {object} obj
 * @return {string}
 */
export function getStringFromObject(keys: any[], obj: any): string {
    let val = "";
    keys.forEach((q, index) => {
        if (index > 0) {
            val += ",";
        }

        val += [index, ".", obj[q]].join("");
    });
    return val;
}

/**
 * Strip sub_header from conversation messages
 * @param {Array} messages
 * @return {Array}
 */
export function stripHeaderFromTranscript(messages: any[]): any {
    messages.filter(({ messageType }) => messageType != "sub_header");
    return messages;
}

// total milliseconds in a day
const _MS_PER_DAY = 1000 * 60 * 60 * 24;

/**
 * Finds difference in number of days betwen two dates
 * @param {Date} date1 javscript date object
 * @param {Date} date2 javscript date object
 * @returns {number} difference in number of days
 */
export function dateDiffInDays(date1: Date, date2: Date): number {
    // Discard the time and time-zone information.
    const utc1 = Date.UTC(date1.getFullYear(), date1.getMonth(), date1.getDate());
    const utc2 = Date.UTC(date2.getFullYear(), date2.getMonth(), date2.getDate());
    return Math.floor((utc2 - utc1) / _MS_PER_DAY);
}

export function sanitizeAndParseJsonData(data: string): any {
    data = data.replace(/'/g, '"');
    data = data.replace(/\\/g, "");
    data = JSON.parse(decodeEntities(data));
    return data;
}

/**
 * Checking the visbility of the component based on the hidden state config 
 * @param state
 * @param hiddenstates
 * @return {boolean}
 */
export function shouldBeVisibleByStateCheck(state: any, hiddenstates: any) : boolean {
    let visibility = true;

    switch (state) {
        case CHAT_STATE.STARTING:

        case CHAT_STATE.PRECHAT:
            if (hiddenstates.indexOf("PreEngagement") != -1) {
                visibility = false;
            }

            break;

        case CHAT_STATE.ENGAGED:
            if (hiddenstates.indexOf("InEngagement") != -1) {
                visibility = false;
            }

            break;

        case CHAT_STATE.ENDED:
            if (hiddenstates.indexOf("PostEngagement") != -1) {
                visibility = false;
            }

            break;
    }

    return visibility;
}

/**
 * if no text prop was set and type is known use a default value
 * Use a hard-coded default values
 * @param propObj
 */
export function returnTextOrDefaultTextByType(propObj) {
    if (propObj.text && propObj.text != "undefined") {
        return propObj.text;
    } 

    switch (propObj.type) {
        case "Chime":
            return "Chime";

        case "Close":
            return "End Chat";

        case "Email":
            return "Email Transcript";

        case "Emoji":
            return "Send Emoji";

        case "Minimize":
            return "Minimize Chat";

        case "Overflow":
            return "Menu";

        case "Print":
            return "Print Transcript";

        case "Send":
            return "Send Message";

        case "Logo":
            return "";

        case "Move":
            return "Move Window";

        default:
            log(`'${propObj.type}' element does not have a 'text' property or a default value. ARIA announcement for this button will not work`);
            break;
    }
}
